import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { EVENTS, Joyride, STATUS, type EventData, type Step } from 'react-joyride'
import { Button } from '../../components/ui/button'
import { useT } from '../../i18n'
import { sellerOnboardingTourI18n } from '../../i18n/onboarding/seller-tour'
import { USE_REAL_API } from '../../lib/api'
import { fetchMe } from '../../lib/auth'
import { resolveCurrentUser } from '../../lib/user-permissions'
import { useMobileNavStore } from '../../store/mobileNav'
import {
  STORE_INTEGRATIONS_QUERY_KEY,
  fetchStoreIntegrations,
} from '../seller/stores/store-integrations-query'
import {
  consumeSellerTourPending,
  readSellerTourSeen,
  writeSellerTourSeen,
} from './seller-tour-state'
import { SellerTourContext } from './seller-tour-context'

export function SellerTourProvider({ children }: { children: ReactNode }) {
  const t = useT(sellerOnboardingTourI18n)
  const navigate = useNavigate()
  const openSidebar = useMobileNavStore((state) => state.openSidebar)
  const [active, setActive] = useState(false)
  const [runId, setRunId] = useState(0)
  const [fallbackOpen, setFallbackOpen] = useState(false)
  const mountedRef = useRef(true)
  const mockUser = useMemo(() => resolveCurrentUser({ role: 'seller' }), [])
  const me = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: fetchMe,
    enabled: USE_REAL_API,
  })
  const stores = useQuery({
    queryKey: STORE_INTEGRATIONS_QUERY_KEY,
    queryFn: fetchStoreIntegrations,
    enabled: active,
  })

  const userId = USE_REAL_API ? me.data?.user.id : mockUser.user_id
  const identityReady = USE_REAL_API ? me.isFetched : true
  const isEligibleSeller = USE_REAL_API
    ? me.data?.active_membership?.org_type === 'BO2'
    : true
  const hasConnectedStore = Boolean(
    stores.isSuccess &&
      stores.data.items
        .filter(
          (store) =>
            !store.mockData ||
            !store.seller_org_id ||
            store.seller_org_id === 'org_sarah_thoughtful' ||
            store.seller_org_id === 'bo2_sarah',
        )
        .some((store) => store.has_access_token),
  )

  const start = useCallback(() => {
    setFallbackOpen(false)
    setRunId((value) => value + 1)
    setActive(true)
  }, [])

  const startManual = useCallback(() => {
    void navigate({ to: '/seller' }).then(() => {
      if (mountedRef.current) start()
    })
  }, [navigate, start])

  const completeActivation = useCallback(() => {
    if (userId) writeSellerTourSeen(userId, 'completed')
    setActive(false)
    setFallbackOpen(false)
  }, [userId])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!identityReady || !userId || !isEligibleSeller) return
    if (!consumeSellerTourPending() || readSellerTourSeen(userId)) return
    const timeout = window.setTimeout(start, 0)
    return () => window.clearTimeout(timeout)
  }, [identityReady, isEligibleSeller, start, userId])

  const steps = useMemo<Step[]>(() => {
    const routeSteps: Step[] = [
      {
        target: '[data-tour="seller-overview"]',
        title: t.steps.overview.title,
        content: t.steps.overview.body,
        placement: 'bottom-start',
      },
      {
        target: '[data-tour="seller-stores-nav"]',
        title: t.steps.storesNav.title,
        content: t.steps.storesNav.body,
        placement: 'right-start',
        before: async () => {
          if (window.matchMedia('(max-width: 1023px)').matches) {
            openSidebar()
            await new Promise<void>((resolve) => window.setTimeout(resolve, 250))
          }
        },
      },
      {
        target: '[data-tour="seller-stores-page"]',
        title: t.steps.storesPage.title,
        content: t.steps.storesPage.body,
        placement: 'bottom-start',
        before: async () => {
          await navigate({ to: '/seller/stores' })
        },
      },
      {
        target: hasConnectedStore
          ? '[data-tour="seller-connected-stores"]'
          : '[data-tour="seller-connect-store"]',
        title: hasConnectedStore
          ? t.steps.connectedStores.title
          : t.steps.connectStore.title,
        content: hasConnectedStore
          ? t.steps.connectedStores.body
          : t.steps.connectStore.body,
        placement: 'top-start',
      },
    ]
    if (!hasConnectedStore) {
      routeSteps.push({
        target: '[data-tour="seller-connect-form"]',
        title: t.steps.connectForm.title,
        content: t.steps.connectForm.body,
        placement: 'top-start',
      })
    }
    return routeSteps
  }, [hasConnectedStore, navigate, openSidebar, t])

  const onEvent = useCallback(
    (event: EventData) => {
      if (event.type === EVENTS.TARGET_NOT_FOUND) {
        setActive(false)
        setFallbackOpen(true)
        return
      }
      if (event.status === STATUS.SKIPPED) {
        if (userId) writeSellerTourSeen(userId, 'skipped')
        setActive(false)
        return
      }
      if (event.status === STATUS.FINISHED) {
        // Completion is persisted only after an OAuth authorize URL succeeds.
        setActive(false)
      }
    },
    [userId],
  )

  const contextValue = useMemo(
    () => ({ active, startManual, completeActivation }),
    [active, completeActivation, startManual],
  )

  return (
    <SellerTourContext.Provider value={contextValue}>
      {children}
      {isEligibleSeller ? (
        <Joyride
          key={runId}
          continuous
          run={active}
          scrollToFirstStep
          steps={steps}
          onEvent={onEvent}
          locale={{
            back: t.back,
            close: t.close,
            last: t.last,
            next: t.next,
            nextWithProgress: `${t.next} ({current}/{total})`,
            open: t.open,
            skip: t.skip,
          }}
          options={{
            backgroundColor: 'var(--color-porcelain)',
            textColor: 'var(--color-ink)',
            primaryColor: 'var(--color-action)',
            overlayColor: 'var(--color-overlay)',
            arrowColor: 'var(--color-porcelain)',
            buttons: ['back', 'close', 'primary', 'skip'],
            closeButtonAction: 'skip',
            showProgress: true,
            blockTargetInteraction: false,
            spotlightPadding: 8,
            spotlightRadius: 10,
            targetWaitTimeout: 3000,
            zIndex: 60,
          }}
          styles={{
            tooltip: {
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-panel)',
              boxShadow: 'var(--shadow-elevated)',
              fontFamily: 'var(--font-sans)',
              maxWidth: 380,
              padding: 20,
            },
            tooltipTitle: { fontSize: 18, fontWeight: 650, lineHeight: 1.25 },
            tooltipContent: { fontSize: 14, lineHeight: 1.55, textAlign: 'left' },
            buttonPrimary: { borderRadius: 'var(--radius-control)', fontWeight: 650 },
            buttonBack: { color: 'var(--color-text-muted)' },
            buttonSkip: { color: 'var(--color-text-muted)' },
          }}
        />
      ) : null}
      {fallbackOpen ? (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-[var(--overlay-bg)] p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="seller-tour-fallback-title"
        >
          <div className="w-full max-w-sm rounded-[var(--radius-panel)] border border-[var(--color-border)] bg-[var(--color-porcelain)] p-5 shadow-[var(--shadow-elevated)]">
            <h2 id="seller-tour-fallback-title" className="text-lg font-semibold text-[var(--color-ink)]">
              {t.fallbacks.targetMissingTitle}
            </h2>
            <p className="mt-2 text-sm leading-6 text-[var(--color-text-muted)]">
              {t.fallbacks.targetMissingBody}
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setFallbackOpen(false)}>
                {t.close}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  setFallbackOpen(false)
                  void navigate({ to: '/seller/stores' })
                }}
              >
                {t.fallbacks.openStores}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </SellerTourContext.Provider>
  )
}
